<template>
  <page-header-wrapper title="GoEasy 在线客服">
    <a-alert v-if="initError" type="error" :message="initError" show-icon style="margin-bottom: 12px" />
    <a-spin :spinning="loading">
      <a-card
        :bordered="false"
        :body-style="{ padding: 0 }"
        @dragover.prevent
        @drop.prevent
      >
        <iframe
          v-if="iframeSrc"
          :key="iframeKey"
          class="cs-iframe"
          :src="iframeSrc"
          title="在线客服工作台"
          frameborder="0"
          allow="microphone; clipboard-read; clipboard-write"
          @load="onIframeLoad"
        />
      </a-card>
    </a-spin>

    <a-drawer
      title="代客创建订单"
      placement="right"
      :width="drawerWidth"
      :visible="orderDrawerVisible"
      :destroy-on-close="true"
      @close="closeOrderDrawer"
    >
      <cs-order-create-form
        v-if="orderDrawerUserId"
        ref="orderCreateForm"
        :initial-user-id="orderDrawerUserId"
        :lock-user="true"
        :compact="true"
        @success="onOrderCreateSuccess"
      />
    </a-drawer>
  </page-header-wrapper>
</template>

<script>
import { getWorkbenchInit, getTransferAgents, csTransfer, csAdminCustomerAssignments, csCustomerProfile, csNotifyCustomerMessage, csHeartbeat, csOffline, csClockOut, csSyncServingAgent, csReportServingAgent } from '@/api/customer-service/order'
import { getCsReadReceipt, reportCsReadReceipt } from '@/api/customer-service/order'
import { csEmbedIndexUrl, csEmbedStaticUrl } from '@/utils/csEmbed'
import CsOrderCreateForm from '@/views/customer-service/order-create/CsOrderCreateForm'

const CS_AGENT_KEY = 'CS_AGENT_INIT'
const CS_SHOP_KEY = 'CS_SHOP_INIT'
const CS_ADMIN_MODE_KEY = 'CS_ADMIN_MODE'
const DEFAULT_CS_AVATAR = csEmbedStaticUrl('images/Avatar-5.png')
const DEFAULT_SHOP_AVATAR = csEmbedStaticUrl('images/shop1.png')

function resolveWorkbenchAvatar(url, fallback) {
  if (!url || String(url).trim() === '' || url === 'null') {
    return fallback
  }
  const s = String(url).trim()
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) {
    return s
  }
  return fallback
}

function buildCsChatHash(query) {
  const customerId = String((query && query.customerId) || '').trim()
  if (!customerId) {
    return '#/conversations'
  }
  const params = new URLSearchParams()
  if (query.name) params.set('name', String(query.name))
  if (query.avatar) params.set('avatar', String(query.avatar))
  if (query.autoAccept === '0') {
    params.set('autoAccept', '0')
  } else {
    params.set('autoAccept', '1')
  }
  const qs = params.toString()
  return `#/conversations/chat/${encodeURIComponent(customerId)}${qs ? '?' + qs : ''}`
}

export default {
  name: 'CustomerServiceWorkbench',
  components: { CsOrderCreateForm },
  data() {
    return {
      loading: true,
      initError: '',
      iframeSrc: '',
      iframeKey: 0,
      agentInitPayload: null,
      orderDrawerVisible: false,
      orderDrawerUserId: null,
      heartbeatTimer: null
    }
  },
  computed: {
    drawerWidth() {
      return Math.min(720, Math.floor(window.innerWidth * 0.92))
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        if (!this.agentInitPayload || this.initError) {
          return
        }
        this.openCustomerFromRoute(true)
      }
    }
  },
  mounted() {
    window.addEventListener('message', this.onCsMessage)
    document.addEventListener('paste', this.onWorkbenchPaste, true)
    getWorkbenchInit().then(res => {
      const d = res.data || {}
      if (!d.csAgentId || !d.shopId) {
        this.initError = '当前账号未配置客服 ID，请在「系统管理 → 用户管理」中设置后重新登录'
        return
      }
      const agentPayload = {
        id: d.csAgentId,
        name: d.agentName || '客服',
        avatar: resolveWorkbenchAvatar(d.agentAvatar, DEFAULT_CS_AVATAR),
        shopId: d.shopId,
        adminMode: !!d.adminMode
      }
      this.agentInitPayload = agentPayload
      sessionStorage.setItem(CS_AGENT_KEY, JSON.stringify(agentPayload))
      sessionStorage.setItem(CS_ADMIN_MODE_KEY, d.adminMode ? '1' : '0')
      sessionStorage.setItem(CS_SHOP_KEY, JSON.stringify({
        id: d.shopId,
        name: d.shopName,
        avatar: DEFAULT_SHOP_AVATAR
      }))
      this.openCustomerFromRoute(false)
      this.startWorkbenchHeartbeat()
      this.$nextTick(() => this.notifyIframeAgentInit(d))
    }).catch(() => {
      this.initError = '加载客服工作台失败'
    }).finally(() => {
      this.loading = false
    })
  },
  activated() {
    if (this.agentInitPayload && !this.initError) {
      this.openCustomerFromRoute(true)
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onCsMessage)
    document.removeEventListener('paste', this.onWorkbenchPaste, true)
    this.stopWorkbenchHeartbeat()
    csOffline().catch(() => {})
    sessionStorage.removeItem(CS_AGENT_KEY)
    sessionStorage.removeItem(CS_SHOP_KEY)
    sessionStorage.removeItem(CS_ADMIN_MODE_KEY)
  },
  methods: {
    openCustomerFromRoute(forceReload) {
      const hash = buildCsChatHash(this.$route.query || {})
      const nextSrc = csEmbedIndexUrl(hash)
      if (!forceReload && this.iframeSrc === nextSrc) {
        return
      }
      if (forceReload || this.iframeSrc !== nextSrc) {
        this.iframeKey += 1
        this.iframeSrc = nextSrc
      }
    },
    onIframeLoad() {
      if (this.agentInitPayload) {
        this.notifyIframeAgentInit(this.agentInitPayload)
      }
      this.postOpenCustomerChat()
    },
    async onWorkbenchPaste(e) {
      // 焦点已在 iframe 内时，由聊天页自己处理，避免重复发送
      const iframe = this.$el && this.$el.querySelector('.cs-iframe')
      if (iframe && document.activeElement === iframe) {
        return
      }
      const clipboard = e && e.clipboardData
      if (!clipboard) {
        return
      }
      const files = []
      if (clipboard.files && clipboard.files.length) {
        for (let i = 0; i < clipboard.files.length; i++) {
          const f = clipboard.files[i]
          if (f && /^image\//.test(f.type || '')) {
            files.push(f)
          }
        }
      }
      if (!files.length && clipboard.items) {
        for (let i = 0; i < clipboard.items.length; i++) {
          const item = clipboard.items[i]
          if (item && item.kind === 'file' && /^image\//.test(item.type || '')) {
            const f = item.getAsFile()
            if (f) {
              files.push(f)
            }
          }
        }
      }
      if (!files.length) {
        return
      }
      e.preventDefault()
      if (!iframe || !iframe.contentWindow) {
        return
      }
      const payload = []
      for (const file of files) {
        const buffer = await file.arrayBuffer()
        payload.push({
          name: file.name || ('paste-' + Date.now() + '.png'),
          type: file.type || 'image/png',
          buffer
        })
      }
      iframe.contentWindow.postMessage({
        type: 'CS_PASTE_IMAGES',
        payload
      }, '*')
    },
    postOpenCustomerChat() {
      const q = this.$route.query || {}
      const customerId = String(q.customerId || '').trim()
      if (!customerId) {
        return
      }
      const iframe = this.$el && this.$el.querySelector('.cs-iframe')
      if (!iframe || !iframe.contentWindow) {
        return
      }
      iframe.contentWindow.postMessage({
        type: 'CS_OPEN_CUSTOMER_CHAT',
        payload: {
          customerId,
          name: q.name || '',
          avatar: q.avatar || '',
          autoAccept: q.autoAccept !== '0'
        }
      }, '*')
    },
    startWorkbenchHeartbeat() {
      this.stopWorkbenchHeartbeat()
      csHeartbeat().catch(() => {})
      this.heartbeatTimer = setInterval(() => {
        csHeartbeat().catch(() => {})
      }, 30000)
    },
    stopWorkbenchHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
    },
    notifyIframeAgentInit(d) {
      const iframe = this.$el && this.$el.querySelector('.cs-iframe')
      if (!iframe || !iframe.contentWindow) {
        return
      }
      iframe.contentWindow.postMessage({
        type: 'CS_AGENT_INIT',
        payload: {
          id: d.id || d.csAgentId,
          name: d.name || d.agentName || 'Customer Support',
          avatar: d.avatar || resolveWorkbenchAvatar(d.agentAvatar, DEFAULT_CS_AVATAR),
          shopId: d.shopId,
          adminMode: !!d.adminMode
        }
      }, '*')
    },
    onCsMessage(event) {
      const data = event.data
      if (!data || !data.requestId) {
        return
      }
      const iframe = this.$el && this.$el.querySelector('.cs-iframe')
      const reply = (result, error) => {
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({ requestId: data.requestId, result, error }, '*')
        }
      }
      if (data.type === 'CS_READ_RECEIPT' || data.type === 'CS_REPORT_READ_RECEIPT') {
        if (!iframe || event.source !== iframe.contentWindow || event.origin !== window.location.origin) return
        const action = data.type === 'CS_READ_RECEIPT'
          ? getCsReadReceipt(String(data.customerGoEasyId || ''))
          : reportCsReadReceipt(String(data.customerGoEasyId || ''), data.throughTimestamp)
        action.then(res => {
          if (!res.success) throw new Error(res.message || '读取回执失败')
          reply(res.data)
        }).catch(e => reply(null, e.message || '读取回执失败'))
      } else if (data.type === 'CS_LOAD_TRANSFER_AGENTS') {
        getTransferAgents().then(res => reply(res.data || [])).catch(e => reply(null, e.message || '加载失败'))
      } else if (data.type === 'CS_LOAD_ADMIN_ASSIGNMENTS') {
        csAdminCustomerAssignments().then(res => reply(res.data || [])).catch(e => reply(null, e.message || '加载失败'))
      } else if (data.type === 'CS_HEARTBEAT') {
        csHeartbeat().then(() => reply({ ok: true })).catch(e => reply(null, e.message || '心跳失败'))
      } else if (data.type === 'CS_OFFLINE') {
        csOffline().then(() => reply({ ok: true })).catch(e => reply(null, e.message || '下线失败'))
      } else if (data.type === 'CS_CLOCK_OUT') {
        const ids = (data.customerGoEasyIds || []).map(id => String(id || '').trim()).filter(Boolean)
        csClockOut(ids).then(() => reply({ ok: true })).catch(e => reply(null, e.message || '下班失败'))
      } else if (data.type === 'CS_TRANSFER') {
        const customerGoEasyId = String(data.customerGoEasyId || '').trim()
        if (!customerGoEasyId) {
          reply(null, 'GoEasy 用户 ID 不能为空')
          return
        }
        csTransfer({
          customerGoEasyId,
          targetCsAgentId: data.targetCsAgentId
        }).then(() => reply({ ok: true })).catch(e => reply(null, e.message || '转接失败'))
      } else if (data.type === 'CS_LOAD_CUSTOMER_PROFILE') {
        const userId = data.userId
        if (!userId) {
          reply(null, '用户 ID 不能为空')
          return
        }
        csCustomerProfile(String(userId)).then(res => reply(res.data || null)).catch(e => reply(null, e.message || '加载失败'))
      } else if (data.type === 'CS_OPEN_CREATE_ORDER') {
        const userId = data.userId
        if (!userId) {
          reply(null, '用户 ID 不能为空')
          return
        }
        this.openOrderDrawer(userId)
        reply({ ok: true })
      } else if (data.type === 'CS_NOTIFY_CUSTOMER_MESSAGE') {
        const payload = data.payload || {}
        const customerGoEasyId = String(payload.customerGoEasyId || '').trim()
        if (!customerGoEasyId) {
          reply(null, 'GoEasy 用户 ID 不能为空')
          return
        }
        csNotifyCustomerMessage(payload).then(res => {
          const pushed = !!(res && (res.data === true || res.data === 'true'))
          reply(pushed)
        }).catch(e => reply(null, e.message || '通知失败'))
      } else if (data.type === 'CS_SYNC_SERVING_AGENT') {
        const customerGoEasyId = String(data.customerGoEasyId || '').trim()
        if (!customerGoEasyId) {
          reply(null, 'GoEasy 用户 ID 不能为空')
          return
        }
        csSyncServingAgent(customerGoEasyId).then(() => reply({ ok: true })).catch(e => reply(null, e.message || '同步失败'))
      } else if (data.type === 'CS_REPORT_SERVING_AGENT') {
        const customerGoEasyId = String(data.customerGoEasyId || '').trim()
        const servingCsAgentId = String(data.servingCsAgentId || '').trim()
        if (!customerGoEasyId || !servingCsAgentId) {
          reply(null, '参数不完整')
          return
        }
        csReportServingAgent(customerGoEasyId, servingCsAgentId).then(() => reply({ ok: true })).catch(e => reply(null, e.message || '上报失败'))
      }
    },
    openOrderDrawer(userId) {
      this.orderDrawerUserId = String(userId)
      this.orderDrawerVisible = true
    },
    closeOrderDrawer() {
      this.orderDrawerVisible = false
      this.orderDrawerUserId = null
    },
    onOrderCreateSuccess() {
      this.closeOrderDrawer()
    }
  }
}
</script>

<style scoped>
.cs-iframe {
  width: 100%;
  min-height: calc(100vh - 180px);
  height: calc(100vh - 180px);
  border: none;
  display: block;
  background: #f0f2f5;
}
</style>
